/**
 * Calculators JS controller.
 *
 * This class provides general purpose functions and variables for calculator form processing.
 */

// Use jQuery as $ within this file scope.
const $ = jQuery;

// Require math.js and chart.js.
const math  = require( 'mathjs' );
const Chart = require( 'chart.js' );

// Require imagesLoaded and provide it with jQuery.
const imagesLoaded = require( 'imagesloaded' );
imagesLoaded.makeJQueryPlugin( $ );

/**
 * T0 = laskentavuoden työtulo (syöttötieto)
 * E0 = laskentavuoden alkuun karttunut eläke (syöttötieto)
 * P0 = laskentahetken säännöllinen palkkatulo (syöttötieto)
 * V = syntymävuosi (syöttötieto)
 * V0 = alin eläkeikä (parametritaulusta)
 * Vi = alinta eläkeikää seuraavat tasaiät (𝑖 = 1,2,3,4,5)
 * V2017 = ikä vuoden 2017 alussa kuukauden tarkkuudella
 * V2026 = ikä vuoden 2026 alussa kuukauden tarkkuudella
 * Ki = ansiotason kasvukerroin
 * Ppi = eläkkeen alkamisvuoteen liittyvä indeksi, joka kuvaa palkkakertoimen reaalista kasvua laskentavuoteen verrattuna (parametritaulusta)
 * Ki = ikä laskentavuoden alussa
 * Evi = laskentavuodesta eläkeikään Vi mennessä karttuva eläke ilman elinaikakerroinvähennystä
 * ev = elinaikakerroin (parametritaulusta)
 * k = laskurikohtainen apuparametri, eläkelaskurissa syntymäkuukausi (syöttötieto)
 * r = iästä riippuva karttumaprosentti (1,7% 53-62 vuotiailla, muutoin 1,5%)
 */

/**
 * Class Calculators. This gets mapped with the keyword Calculators and its
 * docready method is fired automatically on pages where there's a
 * class called 'Calculators' on the html tag.
 */
class Calculators {

    /**
     * The class constructor is for binding class properties.
     */
    constructor() {
    }

    /**
     * Cache DOM elements for use in the methods
     */
    cache() {

        // Store a shorthand reference to translatable strings.
        this.strings = themeData.themeStrings.calculators;

        // Instance of Calculators to fetch shared functions/params from.
        this.validator = Theme.getController( 'Validator' );

        // Keep track of whether we're currently listening for print media.
        this.printListenerOn = false;

        // Content that should be hidden when result containers are printed.
        this.$hideFromPrint = $( '#js-cookie-notice, .ancestor-container, .content-wrapper--main, footer' );

        // Printable results container for calculation results.
        this.$cloneContainer = null;

        // Scrolling element.
        this.scrollingElement = document.scrollingElement || document.documentElement || document.body;

        // Current scroll position.
        this.distanceFromTop = this.scrollingElement.scrollTop;

        // Error messages.
        this.errors = {};

        // Data table tooltips.
        this.dataTooltips = {
            'mela_insurance_cost': this.strings.mela_insurance_cost_tooltip,
            'work_mata_insurance_health_services_cost': this.strings.work_mata_insurance_health_services_cost_tooltip,
            'stand_in_cost': this.strings.stand_in_cost_tooltip
        };

        // Data table notes. This can be populated programmatically before, during, or after
        // the calculations to add extra notes to the data table. Notes are automatically
        // cleared after rendering the data table.
        this.dataNotes = {};

        // Base income factor (2014)
        this.baseIncomeFactor = 1000;

        // Yearly income factor (palkkakerroin, 2025)
        this.yearlyIncomeFactor = 1673;

        // Minimum income resulting in required MYEL insurance (2018)
        this.insuranceRequiredIncome = 3828.13;

        // MYEL insurance base percentage (2024)
        this.MYELInsuranceBasePercentage = 0.2410;

        // MYEL insurance base percentage for age group 53-62 (2024)
        this.MYELInsuranceBasePercentageByAgeGroup = 0.2560;

        // MYEL insurance minimum income (työtulon alaraja, 2025)
        this.MYELInsuranceMinIncome = 32259.19;

        // MYEL insurance maximum income (työtulon yläraja, 2025)
        this.MYELInsuranceMaxIncome = 50693.09;

        // Yearly minimum income ("VTAminimi") (2025)
        this.yearlyMinIncome = 16950.00;

        // MATA insurance base sum (Mata-TA-pohjaosa maatalousyrittäjät, 2024)
        this.MATAInsuranceBaseSum = 12.00;

        // MATA insurance income factor (Mata-TA-kerroin, 2024)
        this.MATAInsuranceIncomeFactor = 0.0085;

        // Leisure time MATA insurance base sum (Mata-TA-pohjaosa, 2024)
        this.leisureMATAInsuranceBaseSum = 18.00;

        // Leisure time MATA insurance income factor (Mata-VA-kerroin, 2024)
        this.leisureMATAInsuranceIncomeFactor = 0.0040;

        // Stand in cost factor (2019)
        this.standInCostFactor = 1;

        // Group life insurance cost (RHV) for enterpreneurs (RHV-maksu-mv, 2024)
        this.groupLifeInsuranceCostEnterpreneurs = 8;

        // Group life insurance cost (RHV) for beneficiaries (2025)
        this.groupLifeInsuranceCostBeneficiaries  = 6;

        // Pension parameters (min pension age + life expectancy factor) per age group (2018)
        // Note that form markup (birth_year min/max attributes) should be in sync with this!
        this.pensionParameters = {
            1950: [ '63 years', 0.98351 ],
            1951: [ '63 years', 0.97914 ],
            1952: [ '63 years', 0.97552 ],
            1953: [ '63 years', 0.97200 ],
            1954: [ '63 years', 0.96800 ],
            1955: [ '63 years + 3 months', 0.96344 ],
            1956: [ '63 years + 6 months', 0.96102 ],
            1957: [ '63 years + 9 months', 0.95722 ],
            1958: [ '64 years', 0.95404 ],
            1959: [ '64 years + 3 months', 0.94984 ],
            1960: [ '64 years + 6 months', 0.94659 ],
            1961: [ '64 years + 9 months', 0.94419 ],
            1962: [ '65 years', 0.94692 ],
            1963: [ '65 years', 0.94759 ],
            1964: [ '65 years', 0.946 ],
            1965: [ '65 years + 1 months', 0.947 ],
            1966: [ '65 years + 2 months', 0.947 ],
            1967: [ '65 years + 4 months', 0.943 ],
            1968: [ '65 years + 6 months', 0.940 ],
            1969: [ '65 years + 7 months', 0.938 ],
            1970: [ '65 years + 6 months', 0.936 ],
            1971: [ '65 years + 9 months', 0.933 ],
            1972: [ '65 years + 11 months', 0.934 ],
            1973: [ '66 years', 0.931 ],
            1974: [ '66 years + 1 months', 0.929 ],
            1975: [ '66 years + 2 months', 0.927 ],
            1976: [ '66 years + 3 months', 0.925 ],
            1977: [ '66 years + 5 months', 0.926 ],
            1978: [ '66 years + 6 months', 0.924 ],
            1979: [ '66 years + 7 months', 0.922 ],
            1980: [ '66 years + 8 months', 0.920 ],
            1981: [ '66 years + 9 months', 0.918 ],
            1982: [ '66 years + 10 months', 0.916 ],
            1983: [ '66 years + 11 months', 0.914 ],
            1984: [ '67 years', 0.912 ],
            1985: [ '67 years + 1 months', 0.911 ],
            1986: [ '67 years + 2 months', 0.909 ],
            1987: [ '67 years + 4 months', 0.910 ],
            1988: [ '67 years + 5 months', 0.908 ],
            1989: [ '67 years + 6 months', 0.906 ],
            1990: [ '67 years + 7 months', 0.905 ],
            1991: [ '67 years + 6 months', 0.903 ],
            1992: [ '67 years + 9 months', 0.902 ],
            1993: [ '67 years + 10 months', 0.901 ],
            1994: [ '67 years + 11 months', 0.899 ],
            1995: [ '68 years', 0.898 ],
            1996: [ '68 years + 1 months', 0.896 ],
            1997: [ '68 years + 2 months', 0.895 ],
            1998: [ '68 years + 3 months', 0.894 ],
            1999: [ '68 years + 4 months', 0.893 ],
            2000: [ '68 years + 4 months', 0.889 ],
            2001: [ '68 years + 5 months', 0.888 ],
            2002: [ '68 years + 6 months', 0.887 ],
            2003: [ '68 years + 7 months', 0.886 ],
            2004: [ '68 years + 8 months', 0.885 ],
            2005: [ '68 years + 9 months', 0.884 ],
            2006: [ '68 years + 10 months', 0.883 ],
            2007: [ '68 years + 11 months', 0.882 ],
            2008: [ '69 years', 0.881 ],
            2009: [ '69 years', 0.878 ],
            2010: [ '69 years + 1 months', 0.877 ],
            2011: [ '69 years + 2 months', 0.877 ],
            2012: [ '69 years + 3 months', 0.876 ],
            2013: [ '69 years + 4 months', 0.875 ],
            2014: [ '69 years + 5 months', 0.875 ],
            2015: [ '69 years + 5 months', 0.872 ],
          };

        this.pensionIndexFactors = {
            2025: 1.000,
            2026: 1.009,
            2027: 1.020,
            2028: 1.029,
            2029: 1.038,
            2030: 1.047,
            2031: 1.057,
            2032: 1.067,
            2033: 1.077,
            2034: 1.088,
            2035: 1.099,
            2036: 1.110,
            2037: 1.121,
            2038: 1.132,
            2039: 1.143,
            2040: 1.154,
            2041: 1.165,
            2042: 1.176,
            2043: 1.188,
            2044: 1.200,
            2045: 1.212,
            2046: 1.224,
            2047: 1.236,
            2048: 1.248,
            2049: 1.260,
            2050: 1.272,
            2051: 1.284,
            2052: 1.297,
            2053: 1.310,
            2054: 1.323,
            2055: 1.336,
            2056: 1.349,
            2057: 1.362,
            2058: 1.375,
            2059: 1.388,
            2060: 1.402,
            2061: 1.416,
            2062: 1.430,
            2063: 1.444,
            2064: 1.458,
            2065: 1.472,
            2066: 1.486,
            2067: 1.501,
            2068: 1.516,
            2069: 1.531,
            2070: 1.546,
            2071: 1.561,
            2072: 1.576,
            2073: 1.591,
            2074: 1.607,
            2075: 1.623,
            2076: 1.639,
            2077: 1.655
        };

        this.lifeExpectancyMales = {
            63: {
                2024: 20.4,
                2025: 20.5,
                2030: 21.1,
                2035: 21.6,
                2040: 22.2,
                2045: 22.7,
                2050: 23.3,
                2055: 23.8,
                2060: 24.3,
                2065: 24.8,
                2070: 25.2,
                2075: 25.7,
                2080: 26.1,
                2085: 26.6,
                2090: 27.0
            },
            64: {
                2024: 19.6,
                2025: 19.7,
                2030: 20.3,
                2035: 20.8,
                2040: 21.4,
                2045: 21.9,
                2050: 22.4,
                2055: 22.9,
                2060: 23.4,
                2065: 23.9,
                2070: 24.3,
                2075: 24.8,
                2080: 25.2,
                2085: 25.7,
                2090: 26.1
            },
            65: {
                2024: 18.8,
                2025: 18.9,
                2030: 19.5,
                2035: 20.0,
                2040: 20.5,
                2045: 21.0,
                2050: 21.6,
                2055: 22.0,
                2060: 22.5,
                2065: 23.0,
                2070: 23.4,
                2075: 23.9,
                2080: 24.3,
                2085: 24.7,
                2090: 25.2
            },
            66: {
                2024: 18.1,
                2025: 18.2,
                2030: 18.7,
                2035: 19.2,
                2040: 19.7,
                2045: 20.2,
                2050: 20.7,
                2055: 21.2,
                2060: 21.7,
                2065: 22.1,
                2070: 22.6,
                2075: 23.0,
                2080: 23.4,
                2085: 23.8,
                2090: 24.2
            },
            67: {
                2024: 17.3,
                2025: 17.4,
                2030: 17.9,
                2035: 18.4,
                2040: 18.9,
                2045: 19.4,
                2050: 19.9,
                2055: 20.3,
                2060: 20.8,
                2065: 21.3,
                2070: 21.7,
                2075: 22.1,
                2080: 22.5,
                2085: 22.9,
                2090: 23.3,
            },
            68: {
                2024: 16.5,
                2025: 16.6,
                2030: 17.1,
                2035: 17.6,
                2040: 18.1,
                2045: 18.6,
                2050: 19.1,
                2055: 19.5,
                2060: 20.0,
                2065: 20.4,
                2070: 20.8,
                2075: 21.2,
                2080: 21.7,
                2085: 22.1,
                2090: 22.4,
            },
            69: {
                2024: 15.8,
                2025: 15.9,
                2030: 16.4,
                2035: 16.9,
                2040: 17.3,
                2045: 17.8,
                2050: 18.2,
                2055: 18.7,
                2060: 19.1,
                2065: 19.6,
                2070: 20.0,
                2075: 20.4,
                2080: 20.8,
                2085: 21.2,
                2090: 21.5,
            },
            70: {
                2020: 15.1,
                2025: 15.2,
                2030: 15.6,
                2035: 16.1,
                2040: 16.6,
                2045: 17.0,
                2050: 17.4,
                2055: 17.9,
                2060: 18.3,
                2065: 18.7,
                2070: 19.1,
                2075: 19.5,
                2080: 19.9,
                2085: 20.3,
                2090: 20.7,
            }
        };

        this.lifeExpectancyFemales = {
            63: {
                2024: 24.1,
                2025: 24.2,
                2030: 24.8,
                2035: 25.3,
                2040: 25.8,
                2045: 26.3,
                2050: 26.8,
                2055: 27.3,
                2060: 27.8,
                2065: 28.2,
                2070: 28.6,
                2075: 29.1,
                2080: 29.5,
                2085: 29.9,
                2090: 30.3,
            },
            64: {
                2024: 23.3,
                2025: 23.4,
                2030: 23.9,
                2035: 24.4,
                2040: 24.9,
                2045: 25.4,
                2050: 25.9,
                2055: 26.4,
                2060: 26.8,
                2065: 27.3,
                2070: 27.7,
                2075: 28.1,
                2080: 28.5,
                2085: 28.9,
                2090: 29.3,
            },
            65: {
                2024: 22.4,
                2025: 22.5,
                2030: 23.0,
                2035: 23.5,
                2040: 24.0,
                2045: 24.5,
                2050: 25.0,
                2055: 25.4,
                2060: 25.9,
                2065: 26.3,
                2070: 26.8,
                2075: 27.2,
                2080: 27.6,
                2085: 28.0,
                2090: 28.4,
            },
            66: {
                2024: 21.5,
                2025: 21.6,
                2030: 22.1,
                2035: 22.6,
                2040: 23.1,
                2045: 23.6,
                2050: 24.1,
                2055: 24.5,
                2060: 25.0,
                2065: 25.4,
                2070: 25.8,
                2075: 26.3,
                2080: 26.7,
                2085: 27.0,
                2090: 27.4,
            },
            67: {
                2024: 20.7,
                2025: 20.8,
                2030: 21.3,
                2035: 21.8,
                2040: 22.2,
                2045: 22.7,
                2050: 23.2,
                2055: 23.6,
                2060: 24.1,
                2065: 24.5,
                2070: 24.9,
                2075: 25.3,
                2080: 25.7,
                2085: 26.1,
                2090: 26.5,
            },
            68: {
                2024: 19.8,
                2025: 19.9,
                2030: 20.4,
                2035: 20.9,
                2040: 21.4,
                2045: 21.8,
                2050: 22.3,
                2055: 22.7,
                2060: 23.2,
                2065: 23.6,
                2070: 24.0,
                2075: 24.4,
                2080: 24.8,
                2085: 25.2,
                2090: 25.5,
            },
            69: {
                2024: 19.0,
                2025: 19.0,
                2030: 19.5,
                2035: 20.0,
                2040: 20.5,
                2045: 20.9,
                2050: 21.4,
                2055: 21.8,
                2060: 22.2,
                2065: 22.7,
                2070: 23.1,
                2075: 23.5,
                2080: 23.9,
                2085: 24.2,
                2090: 24.6,
            },
            70: {
                2024: 18.1,
                2025: 18.2,
                2030: 18.7,
                2035: 19.1,
                2040: 19.6,
                2045: 20.1,
                2050: 20.5,
                2055: 20.9,
                2060: 21.3,
                2065: 21.8,
                2070: 22.2,
                2075: 22.5,
                2080: 22.9,
                2085: 23.3,
                2090: 23.7,
            }
        };
    }

    /**
     * DOM events shared by multiple calculator classes and/or utility methods.
     *
     * @return {void}
     */
    events() {

        // Tooltip events: open or close on click etc.
        $( '.flexible-layout--calculator_layout' )
            .on( 'click.form-tooltip', '.form__tooltip-toggle', e => {

                // Toggle element.
                const $tooltipToggle = $( e.currentTarget );

                // Tooltip bubble.
                let $tooltipBubble = $tooltipToggle
                    .next( '[role=status]' )
                        .children( '.form__tooltip-bubble' );

                if ( $tooltipBubble.length ) {

                    // Remove bubble.
                    $tooltipBubble.remove();

                } else {

                    // Add tooltip content after timeout. According to Inclusive Components, without this
                    // some setups are not able to register the repopulation of the live region.
                    window.setTimeout( function() {

                        // Create new bubble.
                        $tooltipBubble = $( '<span>' );
                        $tooltipBubble
                            .addClass( 'form__tooltip-bubble' )
                            .attr( 'role', 'status' )
                            .text( $tooltipToggle.data( 'content' ) )
                            .appendTo( $tooltipToggle.next( '[role=status]' ) );

                    }, 100 );

                }

            })
            .on( 'keydown.form-tooltip', e => {
                if ( ( e.keyCode || e.which ) === 27 ) {

                    // ESC key, remove bubble.
                    const $form = $( e.currentTarget ).closest( 'form' );
                    $form.find( '.form__tooltip-bubble' ).remove();

                }
            })
            .on( 'blur.form-tooltip', '.form__tooltip-toggle', e => {

                // Remove bubble.
                $( e.currentTarget )
                    .next( '[role=status]' )
                        .children( '.form__tooltip-bubble' )
                        .remove();

            });

         // Clear the form fields.
         $( '.js-form-reset-button' ).on( 'click', e => {

            wp.a11y.speak( this.strings.form_fields_cleared, 'polite' );
        });

    }

    /**
     * Initialize a calculator form.
     *
     * @param {string} formID ID attribute for the form.
     * @return {object|null} jQuery object containing the form, or null if container or form is missing.
     */
    initForm( formID ) {

        // Placeholder for form.
        let $form = null;

        // Form container / wrapper.
        const $formContainer = $( '#' + formID );

        if ( $formContainer.length ) {

            // Fetch form.
            $form = $formContainer.find( '> form:first' );

            if ( $form.length ) {

                // Add hidden input for form ID.
                const IDField  = '_form';
                const $IDField = $( '<input>' );
                $IDField
                    .attr( 'type', 'hidden' )
                    .attr( 'name', IDField )
                    .attr( 'value', formID )
                    .prependTo( $form );

                // Add data-attribute for form ID (for queries).
                $form.data( 'form_id', formID );

                // Show form container.
                $formContainer.show();

                // Check if query string starts with form ID, and the provided ID matches that of current form. The
                // regex replace makes sure that all special characters in the match string are properly escaped, see
                // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions for more details.
                const matchString = '^' + ( '?' + IDField + '=' + formID + '&' ).replace( /[.*+?^${}()|[\]\\]/g, '\\$&' );
                if ( window.location.search && window.location.search.match( matchString ) ) {

                    // Pre-populate the form from the query string.
                    const queryVars = window.location.search.substr( 1 ).split( '&' );
                    queryVars.forEach( value => {

                        // Split to a key and value pair.
                        value            = value.split( '=' );
                        const inputName  = value[0];
                        const inputValue = decodeURIComponent( value[1].replace( /\+/g, ' ' ) );

                        // Pass key and value to updateForm.
                        this.updateForm( $form, inputName, inputValue );

                    });

                    // Store data-attribute 'submit', so that the Calculator class knows to auto-submit the form.
                    $form.data( 'submit', true );

                }

                // Render tooltips.
                this.renderTooltips( $form );

            }

        }

        return $form;

    }

    /**
     * Calculate income for specific area of field or forest using a predefined income group.
     *
     * @param {int} areaRemaining Area that this income is calculated for.
     * @param {int} areaType 1 = field, 2 = forest.
     * @param {int} locationOfForest 1 = Etelä-Suomi, 2 = Keski-Suomi, 3 = Pohjois-Suomi (optional)
     * @return {float} Total sum of income.
     */
    calculateAreaIncome( areaRemaining, areaType, locationOfForest ) {

        // Groups used for defining income for provided area.
        let incomeGroups = [];
        if ( areaType == 1 ) {

            // Parameters required for calculating income from field. This type of income is
            // calculated based on the number of field hectares: first 12 ha results in most
            // income, next 10 ha results in less, and so on. After 62 ha the income remains
            // constant (x * field ha).
            incomeGroups = [
                [ 12, 1175.30 ],
                [ 10, 477.48 ],
                [ 10, 321.34 ],
                [ 30, 101.00 ],
                [ 0, 18.34 ]
            ];

        } else {

            // Parameters required for calculating income from forest. This type of income is
            // calculated based on the number of forest hectares, and specific calculations
            // depend on the location of the forest area.

            // Etelä-Suomi (default)
            incomeGroups = [
                [ 120, 45.86 ],
                [ 280, 22.93 ]
            ];

            if ( locationOfForest && locationOfForest == 2 ) {

                // Keski-Suomi
                incomeGroups = [
                    [ 150, 36.69 ],
                    [ 350, 18.34 ]
                ];

            } else if ( locationOfForest && locationOfForest == 3 ) {

                // Pohjois-Suomi
                incomeGroups = [
                    [ 200, 27.52 ],
                    [ 500, 13.76 ]
                ];

            }

        }

        // Placeholder for calculation result, returned at the end of the function.
        let result = 0;

        // Iterate over income groups to calculate total income.
        $.each( incomeGroups, ( key, value ) => {

            // Bail out early if no countable area remains.
            if ( ! areaRemaining ) {
                return false;
            }

            // Check and parse group parameters.
            const groupAreaMax = parseFloat( value[0]);
            const groupAreaVal = parseFloat( value[1]);
            const groupArea    = groupAreaMax === 0 || areaRemaining < groupAreaMax ? areaRemaining : groupAreaMax;

            // Subtract group area from remaining area.
            areaRemaining -= groupArea;

            // Calculate and format income for this group.
            const groupIncome = groupArea * ( groupAreaVal * ( this.yearlyIncomeFactor / this.baseIncomeFactor ) );

            // Increment total income with the result from this group.
            result += parseFloat( groupIncome );

        });

        return result;

    }

    /**
     * Calculate MYEL insurance. Based on formula 1.6.2.
     *
     * Note: this is used together with the calculateMYELInsuranceM() function.
     *
     * @param {int} income Actual income.
     * @param {int} birthYear Birth year (optional).
     * @param {int} birthMonth Birth month (optional).
     * @param {int} duration Duration in months (optional).
     * @param {int} decimals Decimals for rounding (optional).
     * @return {array} Array with MYEL insurance cost, it's type (false = normal, true = increased), and unit.
     */
    calculateMYELInsurance( income, birthYear, birthMonth, duration, decimals ) {

        // Check which insurance percentage we should use based on age group. Costs are
        // increased for age group 53-62. If birth year isn't available, use default %.
        let ageGroupMatch = false;
        if ( birthYear ) {
            const now = new Date();
            let age = now.getFullYear() - birthYear - 1;
            if ( birthMonth && now.getMonth() > birthMonth ) {
                age++;
            }
            ageGroupMatch = age >= 53 && age <= 62;
        }
        const insurancePercentage = this[ 'MYELInsuranceBasePercentage' + ( ageGroupMatch ? 'ByAgeGroup' : '' ) ];

        // Calculate MYEL insurance cost.
        let MYELInsuranceCost = this.calculateMYELInsuranceM( income );
        MYELInsuranceCost = insurancePercentage * MYELInsuranceCost;

        // Check time period and adjust costs accordingly.
        const result = this.adjustValueForTimePeriod( MYELInsuranceCost, duration, decimals );

        // Return the result of the calculation.
        return [
            result[0],
            ageGroupMatch,
            result[1]
        ];

    }

    /**
     * The "M" function used for calculating MYEL insurance. Based on formula 1.6.2.
     *
     * @param {int} income Actual income.
     * @return {float} MYEL insurance cost.
     */
    calculateMYELInsuranceM( income ) {

        // Required parameters for this calculation.
        const incomeMin = this.MYELInsuranceMinIncome;
        const incomeMax = this.MYELInsuranceMaxIncome;
        const Iv        = this.yearlyIncomeFactor;
        const I2004     = this.baseIncomeFactor;

        // Placeholder for calculation result, returned at the end of the function.
        let result = 0;

        if ( income <= incomeMin ) {

            result = 0.54 * income;

        } else if ( incomeMin < income && income <= incomeMax ) {

            // Note: this calculation is split into multiple parts for readability.
            const calc1 = 0.46 * income + ( Iv / I2004 ) * 3030.12;
            const calc2 = ( Iv / I2004 ) * 11899.95;
            result = this.calculateMYELInsuranceM( incomeMin ) + 0.54 * ( calc1 / calc2 ) * ( income - incomeMin );

        } else if ( income > incomeMax ) {

            result = this.calculateMYELInsuranceM( incomeMax ) + income - incomeMax;

        }

        // Return the result of the calculation.
        return result;

    }

    /**
     * Calculate work time MATA insurance. Based on formula 1.6.3.
     *
     * @param {float} income Total income.
     * @param {bool} requiredInsurance Is insurance required?
     * @param {int} subjectType 1 = not a member of health services, 2 = member of health services, 3 = beneficiary (grant receiver).
     * @param {int} duration Duration in months (optional).
     * @param {int} decimals Decimals for rounding (optional).
     * @param {int} baseSum MATA insurance base.
     * @return {array} MATA insurance cost and unit.
     */
    calculateMATAInsurance( income, insuranceIsRequired, subjectType, duration, decimals, baseSum = 12.00 ) {

        // Required parameters for this calculation.
        const incomeFactor = this.MATAInsuranceIncomeFactor;

        // Local variables depending on input params.
        let k = insuranceIsRequired ? 1 : 1.2;

        if ( insuranceIsRequired === 0 ) {
            k = 1;
        } else if ( parseInt( insuranceIsRequired ) > 0 ) {
            k = 1.2;
        }

        // Basic validation and default value for duration.
        duration = duration && duration > 0 && duration <= 12 ? duration : 12;

        const a = subjectType == 1 ? 1 : ( subjectType == 2 ? 0.8 : 0.1 );

        // Calculate MATA insurance
        const cost = k * a * incomeFactor * income * Math.min( duration, 12 ) / 12 + baseSum;

        // Return the result of the calculation. At this point we check the
        // time period, adjust costs accordingly, and return value and unit.
        const unit = this.adjustUnitForTimePeriod( duration );

        // Return the result of the calculation.
        return [
            cost,
            unit
        ];

    }

    /**
     * Calculate leisure time MATA insurance. Based on formula 1.6.3.
     *
     * Note: duration (in months) is only applicable to beneficiaries (grant receivers). For everyone this value
     * is always 12.
     *
     * @param {float} income Total income.
     * @param {bool} insuranceIsRequired Is insurance required?
     * @param {int} duration Duration in months (1-12), defaults to 12.
     * @return {array} MATA insurance cost and unit.
     */
    calculateLeisureMATAInsurance( income, insuranceIsRequired, duration ) {

        // Rquired parameters for this calculation.
        const baseSum      = this.leisureMATAInsuranceBaseSum;
        const incomeFactor = this.leisureMATAInsuranceIncomeFactor;
        const incomeMin    = this.yearlyMinIncome;

        // Basic validation and default value for duration.
        duration = duration && duration > 0 && duration <= 12 ? duration : 12;

        // Calculate MATA insurance.
        const MATAIncome = insuranceIsRequired ? Math.max( incomeMin, income ) : income;
        const cost = incomeFactor * MATAIncome * ( Math.min( duration, 12 ) / 12 ) + baseSum;

        // Check time period and adjust unit accordingly.
        const unit = this.adjustUnitForTimePeriod( duration );

        // Return the result of the calculation.
        return [
            cost,
            unit
        ];

    }

    /**
     * Calculate stand-in cost.
     *
     * @param {float} income Total income (EUR/year).
     * @return {float} Stand-in cost (EUR/hour).
     */
    calculateStandInCost( income ) {

        // Required parameters for this calculation.
        const standInCostFactor = this.standInCostFactor;

        // Calculate stand-in cost.
        let cost = ( ( 1 / 5400 ) * income + 3 ) * standInCostFactor;
        cost = this.round( cost, 2 );

        // Return the result of the calculation.
        return cost;

    }

    /**
     * Calculate MELA sickness benefit. Based on formula 1.7.2.
     *
     * @param {float} income Total income.
     * @return {float} Sickness benefit per day, rounded to closest EUR cent.
     */
    calculateMELASicknessBenefit( income ) {

        // Round income to closest EUR.
        income = math.round( income );

        // Calculate daily sickness benefit.
        let result = ( 1 / 514 ) * income;
        result = this.round( result, 2 );

        // Return the result of the calculation.
        return result;

    }

    /**
     * Calculate MATA benefit. Based on formula 1.7.3.
     *
     * @param {float} income  Total income.
     * @param {bool} insuranceIsRequired Is insurance required?
     * @return {float} MATA benefit per day, roudned to closest EUR cent.
     */
    calculateMATABenefit( income, insuranceIsRequired ) {

        // Rquired parameters for this calculation.
        const incomeMin = this.yearlyMinIncome;

        // Calculate daily MATA benefit.
        let result = ( 1 / 360 ) * ( insuranceIsRequired ? Math.max ( income, incomeMin ) : income );
        result = this.round( result, 2 );

        // Return the result of the calculation.
        return result;

    }

    /**
     * TODO.
     */
    calculatePensionStartYear( birthYear, birthMonth ) {

        const lowestPensionAge = this.pensionParameters[birthYear][0];

        const lowestPensionYear = parseInt( lowestPensionAge.slice( 0, 2 ) );
        let lowestPensionMonths = 0;

        if ( lowestPensionAge.length > 8 ) {
           lowestPensionMonths = parseInt( lowestPensionAge.slice( 11, 13 ).trim() );
        }

        let pensionYear = birthYear + lowestPensionYear;

        if ( birthMonth + lowestPensionMonths > 11 ) {
            pensionYear = birthYear + lowestPensionYear + 1;
        }

        return pensionYear;
    }

    /**
     * TODO.
     *
     * @param {int} Number before rounding.
     * @return {int} Rounded number.
     */
    roundToNearestTen( number ) {

        const x = parseInt( number / 10 );
        const z = ( ( number / 10 ) - x ) * 10;

        if ( 10 - z < 5 ) {
            number += ( 10 - z );
        } else {
            number -= z;
        }

        return number;
    }

    /**
     * Calculate pension.
     *
     * @param {float} workIncome MYEL (work) income (EUR/year).
     * @param {float} wageIncome Income from work as a wage (EUR/year).
     * @param {float} accumulatedPension Accumulated pension (EUR/month), defaults to 0.
     * @param {int} birthYear Birth year.
     * @param {int} birthMonth Birth month.
     * @return {object} Pension (EUR/month) for min retirement age and next 5 full years.
     */
    calculatePension( workIncome, wageIncome, accumulatedPension, birthYear, birthMonth, pensionIndex ) {

        // Required parameters for this calculation.
        const pensionParameters = this.pensionParameters[ birthYear ];

        // Save original accumulated pension value for later use.
        const initialPension = accumulatedPension;

        // Placeholder for results.
        const results = {};

        // Birth year is in predefined range, calculate actual results.
        if ( pensionParameters ) {

            const pensionYear          = this.calculatePensionStartYear( birthYear, 0 );
            const pensionIndexFactor   = this.pensionIndexFactors[pensionYear];

            // Local variables depending on input params.
            const v                    = birthYear;                                // birth year
            const k                    = birthMonth || 1;                          // birth month
            const P0                   = wageIncome || 0;                          // wage income
            const v0                   = pensionParameters[0];                     // min retirement age

            const T0 = workIncome || 0; // work income
            const minRetirementAgeYear = parseInt( v0 );                           // year part of the min retirement age
            const maxRetirementAgeYear = minRetirementAgeYear + 5;                 // max age to calculate pension until
            let A0                     = T0 + 12 * P0;                             // yearly income total
            let A0pi                   = A0 / 12;
            const v2017                = this.calculateAge( v, k, 2016, 12 );      // age in months 1.1.2017
            const v2026                = this.calculateAge( v, k, 2025, 12 );      // age in months 1.1.2026
            const t0                   = this.calculateAge( v, k, -1, 12 );        // age at the beginning of current year
            const currentAgeYear       = ( new Date() ).getFullYear() - birthYear; // age in years based on the birth year

            if ( pensionIndex ) {
                A0 = ( T0 + 12 * P0 ) * pensionIndexFactor;
                A0pi = A0 / 12;
            }

            // Convert years (minRetirementAge, 53, 63) to months for calculations
            const m53 = 53 * 12;
            const m63 = 63 * 12;
            const mRA = this.convertYearsAndMonthsToMonths( v0 );

            // Add pension years/dates to results.
            results[minRetirementAgeYear] = {
                'label': v0,
                'value': 0
            };
            for ( let i = minRetirementAgeYear + 1; i <= maxRetirementAgeYear; i++ ) {
                results[i] = {
                    'label': i + ' years',
                    'value': 0,
                    'pensionRatio': null,
                    'earningsLevel': null
                };
            }

            // Only display values for future ages.
            if ( minRetirementAgeYear >= currentAgeYear ) {

                // Calculate yearly pension accumulation.
                const E1 = Math.max( mRA - Math.max( Math.min( m63, v2026 ), t0 ), 0 ) + Math.max( Math.min( Math.min( m53, v2026 ), mRA ) - t0, 0 );
                const E2 = Math.max ( Math.max( Math.min( mRA, Math.min( m63, v2026 ) ), m53 ) - Math.max( Math.min ( t0, Math.min( m63, v2026 ) ), m53 ), 0 );
                const E3 = ( 1 - ( 0.4 / 100 ) * ( Math.max( mRA, v2017 ) - mRA ) );
                let E    = ( ( 1.5 / 100 ) * E1 + ( 1.7 / 100 ) * E2 ) * ( A0 / 144 ) * E3;

                // Take life expectancy factor into consideration
                const lifeExpectancyFactor = pensionParameters[1];

                E *= lifeExpectancyFactor;

                if ( pensionIndex ) {
                    accumulatedPension *= pensionIndexFactor;
                }

                // Add accumulated pension and round the result
                E += accumulatedPension || 0;
                E  = this.round( E );

                // Add calculation result to results array.
                results[minRetirementAgeYear].value = E;

                let pensionRatio = this.round( E / ( workIncome / 12 ) * 100 );

                if ( pensionIndex ) {
                    pensionRatio = this.round( E / ( workIncome / 12 * pensionIndexFactor ) * 100 );
                }

                A0pi = this.roundToNearestTen( A0pi );

                // Add pension ratio to earnings and earnings level to results array.
               results[minRetirementAgeYear].pensionRatio  = pensionRatio.toString() + ' %';
               results[minRetirementAgeYear].earningsLevel = A0pi.toString() + ' €/kk';
            }

            let counter = 1;

            for ( let i = minRetirementAgeYear + 1; i <= maxRetirementAgeYear; i++ ) {
                if ( results.hasOwnProperty( i ) ) {

                    if ( pensionIndex ) {
                        A0 = ( T0 + 12 * P0 ) * this.pensionIndexFactors[ pensionYear + counter ];
                        accumulatedPension = initialPension * this.pensionIndexFactors[ pensionYear + counter ];
                        A0pi = A0 / 12;
                    }

                    // Placeholder for calculation result.
                    // Note: calculation returns 0 if year is less than current age in years.
                    let E = 0;

                    // Only display values for future ages.
                    if ( i >= currentAgeYear ) {

                        // Calculate yearly pension accumulation.
                        const t0 = t0 || this.calculateAge( v, k, -1, 12 );
                        const vi = i * 12;
                        const E1 = Math.max( vi - Math.max( Math.min( m63, v2026 ), t0 ), 0 ) + Math.max( Math.min( Math.min( m53, v2026 ), vi ) - t0, 0 );
                        const E2 = Math.max ( Math.max( Math.min( vi, Math.min( m63, v2026 ) ), m53 ) - Math.max( Math.min ( t0, Math.min( m63, v2026 ) ), m53 ), 0 );
                        const E3 = ( 1 - ( 0.4 / 100 ) * ( Math.max( mRA, v2017 ) - vi ) );
                        E        = ( ( 1.5 / 100 ) * E1 + ( 1.7 / 100 ) * E2 ) * ( A0 / 144 ) * E3;

                        // Take life expectancy factor into consideration
                        const lifeExpectancyFactor = pensionParameters[1];
                        E *= lifeExpectancyFactor;

                        // Add accumulated pension and round the result
                        E += accumulatedPension ? accumulatedPension * E3 : 0;
                        E = this.round( E );
                    }

                    let pensionRatio = this.round( E / ( workIncome / 12 ) * 100 );

                    if ( pensionIndex ) {
                        pensionRatio = this.round( E / ( workIncome / 12 * this.pensionIndexFactors[ pensionYear + counter ] ) * 100 );
                    }

                    A0pi = this.roundToNearestTen( A0pi );

                    counter++;

                    // Add calculation result to results array
                    results[i].value = E;
                    results[i].pensionRatio = pensionRatio.toString() + ' %';
                    results[i].earningsLevel = A0pi.toString() + ' €/kk';
                }
            }

        }

        // Info text.
        let info = null;
        if ( pensionParameters ) {
            let lifeExpectancyFactor = pensionParameters[1];
            lifeExpectancyFactor = String( lifeExpectancyFactor ).replace( '.', ',' );
            info = [
                this.strings.life_expectancy_factor + ' ' + lifeExpectancyFactor
            ];
        }

        // Return the result of the calculation and the info text.
        return {
            'data': results,
            'info': info
        };

    }

    /**
     * Calculate partial pension.
     *
     * @param {float} income MYEL (work) income (EUR/year).
     * @param {float} wageIncome Income from work as a wage (EUR/year).
     * @param {float} accumulatedPension Accumulated pension (EUR/month), defaults to 0.
     * @param {int} birthYear Birth year.
     * @param {int} birthMonth Birth month.
     * @param {int} partialPensionAge Age when partial pension starts (months).
     * @param {int} pensionAge Age when full pension starts (months).
     * @param {int} ongoingIncome Ongoing income when partial pension starts.
     * @param {int} ongoingWageIncome Ongoing wage income when partial pension starts.
     * @return {object} Pension (EUR/month) for min retirement age and next 5 full years, info text, heading.
     */
    calculatePartialPension( income, wageIncome, accumulatedPension, birthYear, birthMonth, partialPensionAge, pensionAge, ongoingIncome, ongoingWageIncome ) {

        // Rquired parameters for this calculation.
        var pensionParameters = this.pensionParameters[ birthYear ];

        // Placeholder for results.
        const results = {
            25: {},
            50: {}
        };

        // Birth year is in predefined range, calculate actual results.
        if ( pensionParameters ) {

            // Local variables depending on input params.
            const v     = birthYear;                           // birth year
            const k     = birthMonth || 1;                     // birth month
            const t0    = this.calculateAge( v, k, -1, 12 );   // age at the beginning of current year
            const E0    = accumulatedPension || 0;             // accumulated pension
            const tOVE  = partialPensionAge || 0;              // age in months when partial pension starts
            const b     = pensionAge || 0;                     // age in months when partial pension starts
            const T0    = income || 0;                         // work income
            const P0    = wageIncome || 0;                     // wage income
            const T1    = ongoingIncome || 0;                  // ongoing work income when partial pension starts
            const P1    = ongoingWageIncome || 0;              // ongoing wage income when partial pension starts
            const v0    = pensionParameters[0];                // min retirement age
            const A0    = T0 + 12 * P0;                        // yearly income before partial pension
            const A1    = T1 + 12 * P1;                        // yearly income during partial pension
            const v2017 = this.calculateAge( v, k, 2016, 12 ); // age in months 1.1.2017
            const v2026 = this.calculateAge( v, k, 2025, 12 ); // age in months 1.1.2026
            const ta    = tOVE - math.mod( tOVE - t0, 12 );    // age at the beginning of the year during which partial pension starts
            const ev    = pensionParameters[1];                // life expectancy factor

            if ( tOVE < Math.max( t0, 61 * 12 ) ) {

                // Age when partial pension starts is smaller than current age or 61
                this.validator.errorLog.push([
                    'partial_pension_age_years',
                    this.strings.partial_pension_age_error
                ]);

            } else if ( b < tOVE ) {

                // Age when full pension starts is smaller than age when partial pension starts
                this.validator.errorLog.push([
                    'pension_age_years',
                    this.strings.pension_age_error
                ]);

            } else {

                // Convert years (minRetirementAge, 53, 63) to months for calculations
                const m53 = 53 * 12;
                const m63 = 63 * 12;
                const mRA = this.convertYearsAndMonthsToMonths( v0 );

                // Reusable placeholder parameters for calculations.
                let E1, E2;

                // Calculate partial pension base part.
                E1              = Math.max( ta - Math.max( Math.min( m63, v2026 ), t0 ), 0 ) + Math.max( Math.min( Math.min( m53, v2026 ), ta ) - t0, 0 );
                E2              = Math.max( Math.min( ta, Math.min( m63, v2026 ) ), m53 ) - Math.max( Math.min( t0, Math.min( m63, v2026 ) ), m53 );
                const EOVEpohja = E0 + ( ( 1.5 / 100 ) * E1 + ( 1.7 / 100 ) * E2 ) * ( A0 / 144 );

                // Calculate actual partial pension.
                const EOVE   = EOVEpohja * ( 1 - ( 0.4 / 100 ) * ( Math.max( mRA, v2017 ) - tOVE ) ) * ev;
                const EOVE25 = EOVE * 0.25;
                const EOVE50 = EOVE * 0.50;

                // Calculate additional pension during partial pension.
                E1 = Math.max( b - Math.max( Math.min( m63, v2026 ), ta ), 0 ) + Math.max( Math.min( Math.min( m53, v2026 ), b ) - ta, 0 );
                E2 = Math.max( Math.min( b, Math.min( m63, v2026 ) ), m53 ) - Math.max( Math.min( ta, Math.min( m63, v2026 ) ), m53 );
                const EL = ( ( 1.5 / 100 ) * E1 + ( 1.7 / 100 ) * E2 ) * ( A1 / 144 );

                // Calculate accumulated pension after partial pension.
                const EOVEjalk25 = EOVE25 + ( ( 0.75 * EOVEpohja + EL ) * ( 1 - ( 0.4 / 100 ) * ( Math.max( mRA, v2017 ) - b ) ) ) * ev;
                const EOVEjalk50 = EOVE50 + ( ( 0.50 * EOVEpohja + EL ) * ( 1 - ( 0.4 / 100 ) * ( Math.max( mRA, v2017 ) - b ) ) ) * ev;

                // Add calculation results to results object.
                results[25] = [
                    {
                        'label': [
                            this.strings.partial_pension_label,
                            '25%'
                        ],
                        'value': EOVE25
                    },
                    {
                        'label': this.strings.old_age_pension,
                        'value': EOVEjalk25
                    }
                ];
                results[50] = [
                    {
                        'label': [
                            this.strings.partial_pension_label,
                            '50%'
                        ],
                        'value': EOVE50
                    },
                    {
                        'label': this.strings.old_age_pension,
                        'value': EOVEjalk50
                    }
                ];

            }

        }

        // Info text.
        let info = null;
        if ( pensionParameters ) {
            let lifeExpectancyFactor = pensionParameters[1];
            lifeExpectancyFactor = String( lifeExpectancyFactor ).replace( '.', ',' );
            info = this.strings.life_expectancy_factor + ' ' + lifeExpectancyFactor;
        }

        // Heading text.
        let heading = null;
        if ( pensionParameters ) {
            heading = this.strings.partial_pension_charts_heading
                .replace( '%1$s', this.convertMonthsToYearsAndMonths( partialPensionAge, ' ', this.strings.y, this.strings.m ) )
                .replace( '%2$s', this.convertMonthsToYearsAndMonths( pensionAge, ' ', this.strings.y, this.strings.m ) );
        }

        // Return the result of the calculation, the info text, and the heading text.
        return {
            'data': results,
            'info': info,
            'heading': heading
        };

    }

    /**
     * Convert months to years and months string, such as "x years + y months".
     *
     * Optionally configure strings used for years and months, and define a custom separator, using method params.
     *
     * @param {int} months Number of months.
     * @param {string} separator Separator used between years and months. Defaults to ' + '.
     * @param {string} yearsString String used for years. Defaults to 'years'.
     * @param {string} monthsString String used for months. Defaults to 'months'.
     * @return {string} Years + months.
     */
    convertMonthsToYearsAndMonths( months, separator, yearsString, monthsString ) {

        // Optional parameters.
        separator    = separator || ' + ';
        yearsString  = yearsString || 'years';
        monthsString = monthsString || 'months';

        // Perform conversions.
        const years = Math.floor( months / 12 );
        months = months % 12;

        // Return resulting string.
        return years + ' ' + yearsString + ( months ? separator + months + ' ' + monthsString : '' );

    }

    /**
     * Convert months and years in "x years + y months" format to months.
     *
     * @param {string} yearsAndMonths Years + months.
     * @return {int} Number of months.
     */
    convertYearsAndMonthsToMonths( yearsAndMonths ) {

        // Split string to years and months.
        const yearsAndMonthsArray = yearsAndMonths.split( ' + ' );

        // Calculate the number of months.
        let months = parseInt( yearsAndMonthsArray[0]) * 12;
        if ( yearsAndMonthsArray.length > 1 ) {
            months += parseInt( yearsAndMonthsArray[1]);
        }

        // Return resulting integer.
        return months;

    }

    /**
     * Calculate age in months based on birth year and birth month.
     *
     * @param {int} birthYear Birth year.
     * @param {int} birthMonth Birth month.
     * @param {int} currentYear Current year (optional, defaults to current year).
     * @param {int} currentMonth Current month (optional, defaults to 1).
     * @return {int} Age in months.
     */
    calculateAge( birthYear, birthMonth, currentYear, currentMonth ) {

        // Date object representing date of birth. Note that we subtract 1 from
        // birth month because in JavaScript months start from 0.
        const birthDate = new Date( birthYear, birthMonth - 1, 1 );

        // Date object representing the first day of current month.
        const currentDate = new Date();
        if ( currentYear ) {
            if ( currentYear < 100 ) {

                // Special handling for small numbers: treat as modifiers for current
                // year (5 = current year + 5 years, -1 = current year - 1 year etc.)
                currentYear = ( new Date() ).getFullYear() + currentYear;

            }
            currentDate.setYear( currentYear );
        }
        if ( currentMonth ) {
            currentDate.setMonth( currentMonth - 1 );
        }
        currentDate.setDate( 1 );

        // Difference between current date and birth date in months.
        const dateDiffMonths = currentDate.getMonth() - birthDate.getMonth() +
            ( 12 * ( currentDate.getFullYear() - birthDate.getFullYear() ) );

        // Return date difference in months (age).
        return dateDiffMonths;

    }

    /**
     * Adjust value for specified duration.
     *
     * This function accepts value and duration as parameters, and if duration (in months)
     * is less than 12 (one full year), the final result is adjusted accordingly.
     *
     * @param {float} value Value to be adjusted.
     * @param {int} duration Duration or time period, in months.
     * @param {int|bool} decimals Number of decimals for rounding, or boolean false for no rounding (optional, defaults to 2).
     * @return {array} Adjusted value and matching unit.
     */
    adjustValueForTimePeriod( value, duration, decimals ) {

        // Check time period and adjust value accordingly.
        if ( duration && duration < 12 ) {
            value = ( value * duration ) / 12;
        }

        // Round the value.
        if ( decimals !== false ) {
            decimals = typeof decimals !== 'undefined' && Number.isInteger( decimals ) ? decimals : 2;
            value = this.round( value, decimals );
        }

        // Adjust unit.
        const unit = this.adjustUnitForTimePeriod( duration );

        // Return adjusted value along with the unit.
        return [
            value,
            unit
        ];

    }

    /**
     * Adjust unit for specified duration.
     *
     * @param {int} duration Duration or time period, in months.
     * @return {string} Adjusted unit.
     */
    adjustUnitForTimePeriod( duration ) {

        // Check time period and adjust unit accordingly.
        let unit = this.strings.eur_per_year;
        if ( duration && duration < 12 ) {
            unit = this.strings.eur + '/' + duration + ' ' + this.strings.m;
        }

        // Return adjusted unit.
        return unit;

    }

    /**
     * Date format an input value, but only if it's of expected type and format to begin with.
     *
     * Format value as date if it's a string that contains a number of years, with an optional
     * number of months (e.g. "63 years + 6 months" becomes "63 v 6 kk"). Otherwise return the
     * input value unmodified.
     *
     * @param {*} input Input value.
     * @return {*} Formatted or unmodified input value, depending on the original input value.
     */
    maybeDateFormat( input ) {

        // Check if type of input is string and if it matches a predefined regex clause.
        if ( typeof input === 'string' && input.match( /\d+ years(?: \+ \d+ months)?$/ ) ) {
            input = input
                .replace( 'years', this.strings.y )
                .replace( ' + ', ' ' )
                .replace( 'months', this.strings.m );
        }

        return input;
    }

    /**
     * Wrapper for rounding values to predefined number of decimals.
     *
     * This is needed because math.js round() strips zeros (bad for monetary values), native
     * Math.round() doesn't accept decimals as a param, and so on. Since this is a wrapper,
     * we can also adjust it's behaviour later with ease.
     *
     * @param {float|int|string} value Value to be rounded.
     * @param {int} decimals Number of decimals for rounding (optional, defaults to 0).
     * @param {bool} format Format value for output: enforce trailing zeros and add thousand separators.
     * @return {float|string} Value rounded to set number of decimals. Value is a string if format = true.
     */
    round( value, decimals, format ) {

        // Check if we're dealign with a range.
        const isRange = typeof value === 'string' && value.match( ' - ' );

        // Number of decimals.
        decimals = typeof decimals !== 'undefined' && Number.isInteger( decimals ) ? decimals : 0;

        if ( ! isRange ) {

            // Round the value.
            value = math.round( value, decimals );

        }

        if ( format ) {

            // Convert value to string.
            value = String( value );

            if ( value.match( ' - ' ) ) {

                // Range, split into parts and format separately.
                const values = value.split( ' - ' );
                values.forEach( ( value, index ) => {
                    values[ index ] = this.format( value, decimals );
                });
                value = values.join( ' - ' );

            } else {

                // Single value.
                value = this.format( value, decimals );

            }
        }

        return value;

    }

    /**
     * Format provided value to a set number of decimals and add thousand separators.
     *
     * @param {string} value Value to be formatted.
     * @param {int} decimals Number of decimals for rounding (optional).
     * @return {string} Formatted value.
     */
    format( value, decimals ) {

        if ( decimals ) {

            // Enforce decimals.
            value = math.format( parseFloat( value ), {
                notation: 'fixed',
                precision: decimals
            });
            value = value.replace( '.', ',' );

        }

        // Add thousand separators.
        if ( typeof value !== 'string' ) {
            value = String( value );
        }
        value = value.replace( /\B(?=(\d{3})+(?!\d))/g, ' ' );

        return value;

    }

    /**
     * Validate form data and store in class property validatedData.
     *
     * @param {object} $form jQuery object containing the form being validated.
     * @param {object} validatedData Container for validated data.
     * @return {object} Validated data.
     */
    validateForm( $form, validatedData ) {

        // Remove any existing errors from form and reset Validator error log.
        this.clearErrors( $form );

        // Serialize form.
        const formData = $form.serializeArray();

        // Prepare container for validated data.
        validatedData = validatedData || {};

        // Iterate over serialized form data.
        $.each( formData, ( key, value ) => {

            // Form input name and value.
            var inputName  = value.name;
            var inputValue = value.value;

            // Skip inputs prefixed with an underscore.
            if ( inputName.match( '^_' ) ) {
                return true;
            }

            // Validate value.
            if ( inputValue < 0 ) {
                inputValue = 0;
            } else if ( inputValue ) {
                inputValue = parseFloat( inputValue );
            }

            // Check for fallback values.
            if ( inputValue === '' && typeof validatedData[ inputName ] === 'string' ) {
                inputValue = validatedData[ validatedData[ inputName ] ];
            }

            // Handle native HTML validation by passing input object to Validator.
            const $field = $form.find( '[name="' + inputName + '"]' );
            if ( $field.length ) {
                this.validator.validate( $field[0], true );
            }

            // Store value in local object.
            validatedData[ inputName ] = inputValue;

        });

        // Return validated data.
        return validatedData;

    }

    /**
     * Output form errors.
     *
     * @param {object} jQuery object containing the form.
     */
    outputErrors( $form ) {

        const $errorsWrapper = $form.find( '.calculator-container__errors' );
        $errorsWrapper.removeClass( 'u-hide' );
        const $errorsList    = $errorsWrapper.find( '.calculator-container__errors__list' );

        this.validator.errorLog.forEach( ( item ) => {

            // Get input element.
            const inputName = item[0];
            const $input    = $form.find( '[name="' + inputName + '"]' );

            // Get label and group label (if present).
            let labelText = '';
            const $label  = $form.find( 'label[for=' + $input.attr( 'id' ) + ']' );
            if ( $label.length ) {

                // Label text.
                labelText = $label.text() + ': ';

                // If input is within a form group, prepend group label.
                const $group = $label.parent().parent( '.form__group' );
                if ( $group.length && $group.attr( 'aria-labelledby' ) ) {
                    const $parentLabel = $form.find( '#' + $group.attr( 'aria-labelledby' ) );
                    if ( $parentLabel.length ) {
                        labelText = $parentLabel.text() + ' / ' + labelText;
                    }
                }

            }

            // Error message.
            const errorMessage = item[1];

            // Add error messages.
            const errorID = $input.attr( 'id' ) + '__error';
            const $error  = $( '<li>' );
            $error
                .text( labelText + errorMessage )
                .addClass( 'js-form-error' )
                .appendTo( $errorsList );

            const $contextError = $( '<div>' );
            $contextError
                .addClass( 'js-form-error form__error calculator-container__error' )
                .attr( 'id', errorID )
                .text( errorMessage )
                .insertAfter( $input );
            $input.attr( 'aria-describedby', errorID );

            // Add error class to input for easier styling.
            $input.addClass( 'error' );

        });

        // Scroll to form.
        const distanceFromTop           = $form.offset().top;
        this.scrollingElement.scrollTop = distanceFromTop;

        // Set focus to error container.
        $errorsWrapper.trigger( 'focus' );
    }

    /**
     * Remove errors from specific form and flush Validator error log.
     *
     * @param {object} $form Form as a jQuery object.
     */
    clearErrors( $form ) {

        // Remove any existing errors from form and reset Validator error log.
        this.validator.errorLog = [];

        // Errors list.
        const $errorsWrapper = $form.find( '.calculator-container__errors' );
        $errorsWrapper.addClass( 'u-hide' );

        // Input errors and error list errors.
        $form.find( '.js-form-error' ).remove();
        $form.find( '.error' )
            .removeAttr( 'aria-describedby' )
            .removeClass( 'error' );

    }

    /**
     * Update form values on page to reflect results of validation.
     *
     * @param {object} $form Form as a jQuery object.
     * @param {string} inputName Name of the input.
     * @param {string} inputValue Value of the input.
     */
    updateForm( $form, inputName, inputValue ) {

        // Skip null, empty and undefined values.
        if ( inputValue === null || inputValue === '' || typeof inputValue === 'undefined' ) {
            return false;
        }

        // Sanitize values.
        inputName  = inputName.replace( /[^a-z0-9_-]/gim, '' );
        inputValue = parseInt( inputValue );

        // Skip inputs prefixed with an underscore.
        if ( inputName.match( '^_' ) ) {
            return false;
        }

        // Input element (or elements in case of radio/checkbox).
        const $input = $form.find( '[name="' + inputName + '"]' );

        if ( $input.length === 1 ) {

            // Text or number input.
            $input.val( inputValue );

        } else if ( $input.length > 1 ) {

            // Radio button or checkbox.
            const $inputItem = $input.filter( '[value="' + inputValue + '"]' );
            if ( $inputItem.length === 1 ) {
                $inputItem.prop( 'checked', true );
            }

        }
    }

    /**
     * Render container for calculation results.
     *
     * @param {object} $form jQuery object referencing the form (optional).
     * @return {object} Container element.
     */
    renderResultsContainer( $form ) {

        // Create container element.
        const $container = $( '<div>' );
        $container
            .addClass( 'calculator-container__results-container' )
            .addClass( 'hide-for-print' )
            .attr( 'tabindex', '-1' );

        // Create label for the container.
        const containerNum = $form.siblings( '.calculator-container__results-container' ).length + 1;
        const labelText = this.strings.calculator_results + ' ' + containerNum;
        const $label = $( '<h3>' );
        $label
            .addClass( 'calculator-container__heading' )
            .text( labelText )
            .appendTo( $container );

        if ( $form && $form.length ) {

            // Insert container after form.
            $container.insertAfter( $form );

            // Scroll to container position.
            const distanceFromTop           = $container.offset().top;
            this.scrollingElement.scrollTop = distanceFromTop;

            // Set focus to results container.
            $container.trigger( 'focus' );

        }

        // Return container element.
        return $container;

    }

    /**
     * Render readable list of parameters from validated form data.
     *
     * @param {object} $form jQuery object referencing the form.
     * @param {object} data Validated data.
     * @param {object} formattingRules Field specific formatting rules.
     * @return {object} List of parameters.
     */
    renderParams( $form, data, formattingRules ) {

        // Create container for the params.
        const $list = $( '<ul>' );
        $list.addClass( 'calculator-container__params' );

        // Add list items (params).
        $.each( data, ( key, value ) => {

            // Formatting rules for this param. Possible values:
            // - {string} appendTo: append value from this row to another row.
            // - {string} ext: use specified extension for this row.
            // - {string} label: use specified label for this row.
            // - {bool} unformatted: don't pass value to format() function.
            // - {bool} skip: skip this row.
            // - {bool} skipLabel: don't show label for this row.
            const rules = formattingRules && formattingRules[ key ] ? formattingRules[ key ] : {};

            // Check if "skip" is defined.
            if ( rules.skip ) {
                return true;
            }

            // Custom value for radio buttons.
            const valueTest = typeof value !== 'undefined' && value !== '' ? '[value="' + value + '"]' : '';
            let $input = $form.find( '[name="' + key + '"]' + valueTest );
            if ( $input.length && $input.attr( 'type' ) == 'radio' ) {
                const $label = $form.find( 'label[for=' + $input.attr( 'id' ) + ']' );
                if ( $label.length ) {
                    value = $label.text();
                }
            }

            // Custom value for selects.
            if ( ! $input ) {
                $input = $form.find( '[name="' + key + '"]' );
                if ( $input.length && $input.attr( 'type' ) == 'select' ) {
                    const $option = $input.find( 'option:selected' );
                    value = $option.text();
                }
            }

            // Future earnings select field (ansiokehitys tulevaisuudessa).
            if ( key.match( '_earnings$' ) ) {
                if  ( parseInt( value ) === 0 ) {
                    value = this.strings.earnings_grows;
                } else {
                    value = this.strings.earnings_stays;
                }
            }

            if ( $.isNumeric( value ) ) {

                if ( key.match( '_month$' ) ) {

                    // Param ends with _month. Convert month digits to human-readable names.
                    const month = themeData.themeStrings.months[ value ].long;
                    value = month[0].toUpperCase() + month.slice( 1 );

                } else if ( ! key.match( '_years?$' ) && ! rules.unformatted ) {

                    // Param doesn't end with _month, _year, or _years, so not a month or year value.
                    // Add thousand separators.
                    value = this.format( value, null, true );
                }
            }

            if ( typeof value !== 'undefined' && value != '' ) {

                // Custom extension from formatting rules.
                if ( rules.ext ) {
                    value += ' ' + this.strings[ rules.ext ];
                }

                if ( rules.appendTo ) {

                    // "appendTo" is defind, append value to another list item.
                    const $parentItem = $list.find( 'li[data-key=' + rules.appendTo + ']' );
                    if ( $parentItem.length ) {
                        $parentItem.text( $parentItem.text() + ' ' + value );
                    }

                } else {

                    // Add new item to the list.
                    const $item = $( '<li data-key="' + key + '">' );
                    const param = this.strings[ key ] || key;
                    if ( param ) {
                        let label = '';
                        if ( ! rules.skipLabel ) {
                            label = rules.label ? ( this.strings[ rules.label ] || rules.label ) : param;
                            label += ': ';
                        }
                        $item
                            .text( label + value )
                            .appendTo( $list );
                    }

                }

            }

        });

        // Return the container element.
        return $list;

    }

    /**
     * Render data table from given data rows.
     *
     * @param {object} rows Data rows to output.
     * @param {int} precision Default precision for values (optional).
     * @return {object} Reference to data container.
     */
    renderData( rows, precision, rowsToRemove ) {

        if ( ! rowsToRemove ) {
            rowsToRemove = [];
        }

        // Create container for the data.
        const $container = $( '<div>' );
        $container.addClass( 'table calculator-container__table' );

        // Create table for the data.
        const $table = $( '<table>' );
        $table.appendTo( $container );

        // Keep track of notes.
        let noteCount = 0;

        // Append data rows to the container.
        $.each( rows, ( key, value ) => {

            // Skip this iteration if value is null or it is in rowsToRemove array.
            if ( value === null || Array.isArray( value ) && value[0] === null || rowsToRemove.includes( key ) ) {
                return true;
            }

            // Unit and value.
            let unit = this.strings.eur_per_year;
            if ( Array.isArray( value ) ) {

                // Value is an array, so we assume that index 1 defines the unit. If
                // said unit contains '/', we assume it to be in human-readable form.
                unit = value[1].match( '/' ) ? value[1] : this.strings[value[1]];
                value = value[0];

            }
            if ( typeof precision !== 'undefined' && Number.isInteger( precision ) ) {

                // If precision is set, round value to said precision, and display
                // same amount of digits after dot, retaining trailing zeros.
                value = this.round( value, precision, true );

            }

            // Add new row to the container.
            const $row = $(
                '<tr>' +
                '<th>' + this.strings[key] + '</th>' +
                '<td>' + value + ' ' + unit + '</td>' +
                '</tr>'
             );
             $row.appendTo( $table );

            if ( this.dataTooltips.hasOwnProperty( key ) ) {

                // There's a tooltip for this key, so add it to our newly created row.
                const $tooltip = $( '<span>' );
                $tooltip
                    .addClass( 'form__tooltip' )
                    .text( this.dataTooltips[ key ])
                    .appendTo( $row.find( 'td:last' ) );

                // We want tooltips to be displayed on PDF print version.
                const $tooltipForPrint = $( '<span>' );
                $tooltipForPrint
                    .addClass( 'form__tooltip--for-print' )
                    .text( this.dataTooltips[ key ].replace( '%%', '%' ) )
                    .appendTo( $row.find( 'td:last' ) );
            }

            if ( this.dataNotes.hasOwnProperty( key ) ) {

                // Check if duplicate note already exists and reuse its number if possible.
                let useExistingNote = false;
                const $existingNotes = $table.siblings( '.calculator-container__info' );
                if ( $existingNotes.length ) {
                    $existingNotes.each( ( index, note ) => {

                        // Create jQuery object. Clone allows us to remove note num.
                        const $note = $( note ).clone();

                        // Grab note num.
                        const $noteNum = $note.find( 'span' );
                        const noteNum = $noteNum.text();
                        $noteNum.remove();

                        // Check if text is a match using a clone to remove note num.
                        if ( $note.text() == this.dataNotes[key]) {
                            useExistingNote = noteNum;
                        }

                    });
                }

                // There's a note for this key, so add the number of the note to the row.
                const $noteNum = $( '<span>' );
                $noteNum
                    .addClass( 'calculator-container__note-num' )
                    .text( useExistingNote || ++noteCount )
                    .appendTo( $row.find( 'td:last' ) );

                if ( ! useExistingNote ) {

                    // Add the contents of the note after the table.
                    const $note = $( '<p>' );
                    $note
                        .addClass( 'calculator-container__info' )
                        .text( this.dataNotes[ key ])
                        .prepend( $noteNum.clone() )
                        .insertAfter( $table );

                }

            }

        });

        // Render tooltips.
        this.renderTooltips( $container );

        // Clear notes once we're done rendering the data table.
        this.dataNotes = {};

        // Return the container element.
        return $container;

    }

    /**
     * Render data table from given data rows.
     *
     * @param {object} rows Data rows to output.
     * @param {string} title Title for the table.
     * @return {object} Reference to data container.
     */
    renderGraphTable( rows, title ) {

        // Create container for the data.
        const $container = $( '<div>' );
        $container.addClass( 'table calculator-container__table calculator-container__chart__table' );

        if ( title ) {

            // Add chart title.
            const $title = $( '<h4>' );
            $title
                .addClass( 'calculator-container__chart__graph-title' )
                .text( title )
                .appendTo( $container );
        }

        // Create table for the data.
        const $table = $( '<table>' );
        $table.appendTo( $container );

        // Add thead to the table.
        const $thead = $(
            '<thead>' +
            '<tr>' +
            '<th>' + this.strings.age_at_the_start_of_retirement + '</th>' +
            '<th>' + this.strings.estimated_monthly_income_before_pension + '</th>' +
            '<th>' + this.strings.pension + ', ' + this.strings.eur_per_month + '</th>' +
            '<th>' + this.strings.pension_in_relation_to_the_previous + '</th>' +
            '</tr>' +
            '</thead>'
        );
        $thead.appendTo( $table );

        // Append data rows to the container.
        $.each( rows, ( key, value ) => {

            // Skip this iteration if value is null.
            if ( value === null || Array.isArray( value ) && value[0] === null ) {
                return true;
            }

            // Add new row to the container.
            const $row = $(
                '<tr>' +
                '<td>' + this.maybeDateFormat( value.label ) + '</td>' +
                '<td>' + value.earningsLevel.split( ' ' )[0] + '</td>' +
                '<td>' + value.value + '</td>' +
                '<td>' + value.pensionRatio + '</td>' +
                '</tr>'
             );
             $row.appendTo( $table );
        });

        // Return the container element.
        return $container;

    }

    /**
     * Render chart from provided data.
     *
     * @param {object} data Object containing displayed data.
     * @param {string} title Title (label) for the chart (optional).
     * @param {object} options Extra options for the chart (optional).
     * @return {object} Reference to chart container.
     */
    renderChart( data, title, options ) {

        // Placeholder for options.
        options = options || {};

        // Create container for the chart.
        const $container = $( '<div>' );
        $container.addClass( 'calculator-container__chart' );

        if ( title ) {

            // Add chart title.
            const $title = $( '<h4>' );
            $title
                .addClass( 'calculator-container__chart-title' )
                .text( title )
                .appendTo( $container );

        }

        // Create wrapper for canvas. Mostly required for print support.
        const $wrapper = $( '<div>' );
        $wrapper
            .addClass( 'calculator-container__chart-wrapper' )
            .appendTo( $container );

        // Create chart canvas.
        const $canvas = $( '<canvas>' );
        $canvas.appendTo( $wrapper );

        // Container for chart labels and data.
        const chartLabels = [];
        const chartData   = [];

        // Create list for screen reader content.
        const $list = $( '<ul>' );
        $list.appendTo( $canvas );

        $.each( data, ( key, item ) => {

            // Item label.
            let label = item.label;

            const earningsLevel = item.earningsLevel;
            const pensionRatio  = item.pensionRatio;

            // Format label, but only if it's a string that contains a number of years, with an optional
            // number of months (e.g. "63 years + 6 months" becomes "63 v 6 kk").
            label = this.maybeDateFormat( label );

            // Format value.
            const value = this.round( item.value );

            // Define unit.
            const unit = this.strings.eur_per_year;

            // Add list item.
            const $item = $(
                '<li>' + label + ': ' + value + ' ' + unit + ', ' + earningsLevel + ', ' + pensionRatio + '</li>'
            );
            $item.appendTo( $list );

            if ( earningsLevel !== undefined && pensionRatio !== undefined ) {
                label = [
                    label,
                    earningsLevel,
                    pensionRatio
                ];
            }

           // Add data to chart.
           chartLabels.push( label );
           chartData.push( value );

        });

        // Create chart.
        // TODO: implement minBarLength once it's available via stable chart.js release.
        const canvas = $canvas[0];
        Chart.defaults.global.defaultFontSize = 14;
        new Chart( canvas, {
            type: 'bar',
            data: {
                labels: chartLabels,
                datasets: [ {
                    label: null,
                    data: chartData,
                    datalabels: {
                        align: 'start',
                        anchor: 'end',
                        color: 'white',
                        font: {
                            size: 12
                        }
                    },
                    backgroundColor: 'rgba(51, 51, 153, 0.8)'
                } ]
            },
            options: {
                legend: {
                    display: false
                },
                scales: {
                    yAxes: [ {
                        ticks: {
                            beginAtZero: true
                        }
                    } ],
                    xAxes: [ {
                        barPercentage: _.get( options, 'scales.xAxes[0].barPercentage', 0.9 ),
                        categoryPercentage: _.get( options, 'scales.xAxes[0].categoryPercentage', 0.9 )
                    } ]
                }
            }
        });

        // Return the container element.
        return $container;

    }

    /**
     * Render tooltips.
     *
     * @param {object} $parent jQuery object referencing the element to find tooltips from.
     */
    renderTooltips( $parent ) {

        const $tooltips = $parent.find( '.form__tooltip' );
        if ( $tooltips.length ) {

            // Create tooltips.
            $tooltips.each( ( index, element ) => {

                // Create jQuery object for element.
                const $element = $( element );

                // Add class to parent for styling.
                $element.parent().addClass( 'with-tooltip' );

                // Create tooltip container.
                const $tooltipContainer = $( '<span>' );
                $tooltipContainer
                    .addClass( 'form__tooltip-container' )
                    .addClass( 'hide-for-print' )
                    .insertAfter( $element );

                // Create tooltip toggle.
                const $tooltipToggle = $( '<button>' );
                $tooltipToggle
                    .attr( 'type', 'button' )
                    .attr( 'aria-label', this.strings.tooltip_label )
                    .data( 'content', $element.text().replace( '%%', '%' ) )
                    .addClass( 'form__tooltip-toggle form__input-button form__input-button--info' )
                    .appendTo( $tooltipContainer );

                // Create live region.
                const $tooltipLiveRegion = $( '<span>' );
                $tooltipLiveRegion
                    .attr( 'role', 'status' )
                    .appendTo( $tooltipContainer );

                // Remove original element from DOM.
                $element.remove();

            });

        }

    }

    /**
     * Render heading within suitable container.
     *
     * @param {string} headingText Heading text.
     * @return {(object|undefined)} Reference to heading container, or undefined if no heading text was provided.
     */
    renderHeading( headingText ) {

        if ( headingText.length ) {

            // Create info text container, append to specified element, and set text(s).
            const $container = $( '<h4>' );
            $container
                .addClass( 'calculator-container__inner-heading' )
                .text( headingText );

            // Return the container element.
            return $container;

        }

    }

    /**
     * Render info text within suitable container.
     *
     * @param {array} infoTexts Infotexts as an array.
     * @param {string} glue Glue used for joining infotexts.
     * @return {(object|undefined)} Reference to info text container, or undefined if no info texts were provided.
     */
    renderInfo( infoTexts, glue ) {

        if ( infoTexts.length ) {

            // Define glue string.
            glue = glue || ' ';

            // Filter and sanitize info texts array.
            infoTexts = infoTexts.filter( Boolean );
            infoTexts.forEach( ( value, key ) => {
                infoTexts[key] = _.escape( value );
            });

            if ( infoTexts.length ) {

                // Create info text container, append to specified element, and set text(s).
                const $container = $( '<p>' );
                $container
                    .addClass( 'calculator-container__info' )
                    .html( infoTexts.join( glue ) );

                // Return the container element.
                return $container;

            }

        }

    }

    /**
     * Render action buttons for calculation results container.
     *
     * @param {object} $form jQuery object referencing the form.
     * @return {object} Reference to action buttons.
     */
    renderButtons( $form ) {

        // Create container for buttons.
        const $buttons = $( '<p>' );
        $buttons.addClass( 'form__button-wrapper' );

        // Create email button.
        const $buttonMail = $( '<button>' );
        $buttonMail
            .attr( 'type', 'button' )
            .addClass( 'form__input-button form__input-button--mail' )
            .addClass( 'hide-for-print' )
            .text( this.strings.email_results )
            .on( 'click', e => {

                // Stop propagation.
                Theme.Common.stop( e );

                // Calculator container.
                const $componentContainer = $buttons.closest( '.flexible-layout--calculator_layout' );

                // Mailto subject.
                const resultsHeading    = $buttons
                    .closest( '.calculator-container__results-container' )
                        .children( '.calculator-container__heading:first' )
                        .text();
                const calculatorHeading = $componentContainer
                    .find( '.flexible-layout__header__heading:first' )
                    .text();
                const mailtoSubject     = encodeURIComponent( calculatorHeading + ' - ' + resultsHeading );

                // Mailto body.
                const formData   = $form
                    .find( ':input' )
                    .filter( ( index, element ) => {
                        return $( element ).val() != '';
                    })
                    .serialize();
                const pageURL    = window.location.protocol + '//' + window.location.host + window.location.pathname;
                const bodyText   = this.strings.email_body_text + ': ';
                const mailtoBody = encodeURIComponent( bodyText + pageURL + '?' + formData );

                // Trigger mailto feature.
                window.location.href = 'mailto:?subject=' + mailtoSubject + '&body=' + mailtoBody;

            })
            .appendTo( $buttons );

        // Create print button.
        const $buttonPrint = $( '<button>' );
        $buttonPrint
            .attr( 'type', 'button' )
            .addClass( 'form__input-button form__input-button--print' )
            .addClass( 'hide-for-print' )
            .text( themeData.themeStrings.print )
            .on( 'click', e => {

                // Stop propagation.
                Theme.Common.stop( e );

                // Print container.
                this.renderPrintContainer(
                    $buttonPrint.closest( '.calculator-container__results-container' ),
                    true
                );

            })
            .appendTo( $buttons );

        // Return button container.
        return $buttons;

    }

    /**
     * Render extra content.
     *
     * @param {object} $form jQuery object referencing the form.
     * @param {object} $resultsContainer jQuery object referencing the results container.
     * @return {object|null} Reference to extra content container or null if no extra content was found.
     */
    renderExtra( $form, $resultsContainer ) {

        // Form container element.
        const $formContainer = $form.parent();

        // Fetch extra content.
        const $extra = $formContainer.next( '.calculator-container__extra' );

        // Placeholder for return value.
        let $container = null;

        if ( $extra && $extra.length ) {

            // Clone and append extra content to results container.
            $container = $extra.clone( true );
            $container
                .show()
                .appendTo( $resultsContainer );

        }

        // Return container or null.
        return $container;

    }

    /**
     * Render printable container for results.
     *
     * @param {object} $container jQuery object referencing the results container.
     * @param {bool} canvasToImage Convert canvas elements to images?
     */
    renderPrintContainer( $container, canvasToImage ) {

        // Store current scroll position.
        this.distanceFromTop = this.scrollingElement.scrollTop;

        // Create a printable results container by cloning original container.
        this.$cloneContainer = $container.clone( );
        this.$cloneContainer
            .addClass( 'calculator-container__results-container--printable' )
            .removeClass( 'hide-for-print' );

        // Create proper headline for printable container.
        const $parentHeading = $container
            .closest( '.flexible-layout--calculator_layout' )
            .find( '.flexible-layout__header__heading' );
        const $heading = this.$cloneContainer.find( '.calculator-container__heading' );
        $heading.text( $parentHeading.text() + ' – ' + $heading.text() );

        // Prepend printable container to main content.
        this.$cloneContainer.prependTo( $( '#main-content' ) );

        // Find any canvas elements from printable container.
        const $canvasItems = $container.find( 'canvas' );
        const canvasLength = $canvasItems.length;

        if ( canvasLength ) {

            $canvasItems.each( ( index, oldCanvas ) => {

                // jQuery canvas object.
                const $newCanvas = this.$cloneContainer.find( 'canvas:first' );

                if ( canvasToImage ) {

                    // Convert canvas elements to images.

                    // Create image from the original canvas.
                    const $image = $( '<img>' );
                    $image.attr( 'src', oldCanvas.toDataURL() );

                    // Replace new canvas with image.
                    $newCanvas.replaceWith( $image );

                } else {

                    // Redraw canvas content.

                    // HTML canvas object.
                    const newCanvas  = $newCanvas[0];

                    // Dimensions for new canvas.
                    const maxWidth   = 500;
                    newCanvas.width  = parseInt( $newCanvas.css( 'width' ) );
                    newCanvas.height = parseInt( $newCanvas.css( 'height' ) );
                    if ( newCanvas.width > maxWidth ) {
                        const aspectRatio = newCanvas.height / newCanvas.width;
                        newCanvas.width = maxWidth;
                        newCanvas.height = parseInt( newCanvas.width * aspectRatio );
                    }

                    // Redraw canvas content with proper dimensions.
                    const newContext = newCanvas.getContext( '2d' );
                    newContext.drawImage( oldCanvas, 0, 0, newCanvas.width, newCanvas.height );

                }

            });

            // Print the container, but wait for images to load first.
            $container.imagesLoaded( () => {
                this.printContainer();
            });

        } else {

            // Print the container right away.
            this.printContainer();

        }

    }

    /**
     * Print results container.
     */
    printContainer() {

        // Hide content we don't want to be included in the print version.
        this.$hideFromPrint.addClass( 'hide-for-print' );

        if ( window.matchMedia && ! this.printListenerOn ) {

            // Start listening for print media changes.
            const printQuery = window.matchMedia( 'print' );
            printQuery.addListener( mql => {
                if ( ! mql.matches ) {
                    this.cleanupPrintContainer();
                }
            });

            // Be nice to Edge.
            window.onafterprint = ( () => {
                this.cleanupPrintContainer();
            });

            // Remember that we're listening to media changes.
            this.printListenerOn = true;

        }

        // Print page.
        window.print();

        if ( ! window.matchMedia || ! this.printListenerOn ) {

            // No matchMedia support in browser, perform cleanup right away.
            this.cleanupPrintContainer();

        }

    }

    /**
     * Cleanup function, meant to be executed after printContainer.
     */
    cleanupPrintContainer() {

        // Remove cloned container and restore other elements to their earlier state.
        this.$cloneContainer.remove();
        this.$hideFromPrint.removeClass( 'hide-for-print' );

        // Restore original scroll position.
        this.scrollingElement.scrollTop = this.distanceFromTop;

    }

    /**
     * Run when the document is ready.
     */
    docReady() {
        this.cache();
        this.events();
    }

}

// Export the class reference.
module.exports = Calculators;



// WEBPACK FOOTER //
// ./assets/scripts/calculators.js